import { useEffect, useState } from 'react';

/**
 * 모의투자 차트에 넘길 색. (18단계)
 *
 * Recharts 는 색을 props 로 받아 Tailwind 클래스를 줄 수 없다.
 * 그래서 CSS 토큰의 실제 값을 읽어 넘긴다.
 * ledger/chartColors.ts 와 같은 이유이고 구조도 같다.
 *
 * 🔴 왜 ledger 것을 가져다 쓰지 않았나
 *   그쪽 토큰은 수입·지출·순액이라 이름이 맞지 않고,
 *   무엇보다 --rise / --fall 을 쓴다. 그 둘은 "올랐나 내렸나" 를 뜻해서
 *   프로필 곡선에 쓰면 하이리스크 선이 빨갛다는 이유로 손실로 읽힌다.
 *   프로필은 정체성이지 방향이 아니다.
 */

function cssVar(name: string, fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

export interface PaperChartColors {
  /** 격자선 */
  line: string;
  /** 축 라벨 글자 */
  axis: string;
  /** 안전 프로필 곡선 */
  safe: string;
  /** 분할익절 프로필 곡선 (2026-08-20) */
  moderate: string;
  /** 하이리스크 프로필 곡선 */
  aggressive: string;
  /** 저점매수 곡선 (2026-08-28). 라이트는 짙은 보라, 다크는 밝은 연보라다 */
  band: string;
  /**
   * 저점매수(리스크) 곡선. (2026-09-10)
   *
   * 🔴 그전에는 band 를 함께 썼다. 두 계좌가 같은 색이라 비교 차트에서
   *   어느 선이 어느 계좌인지 가릴 수 없었다. 보라 옆의 자홍이라
   *   "같은 계열의 다른 변형" 이라는 것은 색으로도 남는다.
   */
  bandRisk: string;
  /**
   * 저점매수(챌린저) 곡선. (2026-09-18, 3회차부터)
   *
   * 리스크(자홍) 옆의 청록이다 - 같은 저점매수 계열이지만 규칙이 셋이나 달라
   * 보라·자홍 계열에 더 얹으면 세 선을 눈으로 가릴 수 없다.
   */
  bandChallenger: string;
  /** 국내 스윙(6개월) 곡선 (2026-10-10 · 4회차부터) */
  swingA: string;
  /** 국내 스윙(3개월) 곡선 */
  swingC: string;
  /** 목표선. 계열이 아니라 주석이라 회색이다 */
  goal: string;
  /** 툴팁 배경 */
  surface: string;
}

/** 폴백은 라이트 값이다. 테스트 환경에서 변수를 못 읽어도 그려진다. */
const FALLBACK: PaperChartColors = {
  line: '#e2e8f0',
  axis: '#94a3b8',
  safe: '#3182f6',
  moderate: '#0d9488',
  aggressive: '#d97706',
  band: '#6b21a8',
  bandRisk: '#be185d',
  bandChallenger: '#0f766e',
  swingA: '#a16207',
  swingC: '#65a30d',
  goal: '#94a3b8',
  surface: '#ffffff',
};

function read(): PaperChartColors {
  return {
    line: cssVar('--line', FALLBACK.line),
    axis: cssVar('--fg-subtle', FALLBACK.axis),
    safe: cssVar('--paper-safe', FALLBACK.safe),
    moderate: cssVar('--paper-moderate', FALLBACK.moderate),
    aggressive: cssVar('--paper-aggressive', FALLBACK.aggressive),
    band: cssVar('--paper-band', FALLBACK.band),
    bandRisk: cssVar('--paper-band-risk', FALLBACK.bandRisk),
    bandChallenger: cssVar('--paper-band-challenger', FALLBACK.bandChallenger),
    swingA: cssVar('--paper-swing-a', FALLBACK.swingA),
    swingC: cssVar('--paper-swing-c', FALLBACK.swingC),
    // 🔴 목표선은 계열이 아니다. 계열 색을 주지 않는다
    //   색을 주면 "또 하나의 계좌" 로 읽힌다. 회색 파선으로 배경에 둔다.
    goal: cssVar('--fg-subtle', FALLBACK.goal),
    surface: cssVar('--surface', FALLBACK.surface),
  };
}

export function usePaperChartColors(): PaperChartColors {
  const [colors, setColors] = useState<PaperChartColors>(read);

  useEffect(() => {
    // 테마를 바꾸는 경로가 여럿이라 DOM 을 직접 본다. (chartColors.ts 와 같은 이유)
    const observer = new MutationObserver(() => setColors(read()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return colors;
}
